import { toDate } from "../../utils/date"
import { isDate } from "../../utils/utils"
import { PopupContentProps } from "../Popup"
import { classes } from "../react"
import { DateChangeType } from "./DatePicker"
import { MonthSelector } from "./MonthSelector"
import { useRangeFunctions } from "./range"

interface Props {
    /** Currently selected date */
    date?: Date

    /**
     * This is called when the date has changed
     *
     * If the function returns "true", then the popup will close
     *
     * The "changeType" parameter can be used by the consumer to know how
     * the date was change, and decide to either close or leave the popup open
     *
     * For instance, when browsing through years/months, this actually changes
     * the current date (there is no distinct state for that),
     * but we generally do not want the popup to close in such cases
     *
     */
    setDate: (value: Date, changeType?: DateChangeType) => boolean | void

    /**
     * Default date, used when "date" is not set
     * Note: not quite sure when/how/if this is used in retrospect
     * We should probably review this at some point
     **/
    defaultDate?: Date

    /*
     * rangeClosingDate is used if the date picker is used to indicate an interval between
     * date and rangeClosingDate
     */
    rangeClosingDate?: Date
}

/**
 * Implements the calendar part for the <DateTime> component
 * The calendar is displayed in a popup when a user clicks the button displayed next to the input
 * (see "DatePicker.tsx" for this)
 * It displays a monthly view of dates, and can be used to browse through dates,
 * and select a date by clicking
 */
export const DatePickerPopup = (props: Props & PopupContentProps) => {
    const {
        date,
        defaultDate,
        setDate,
        rangeClosingDate,
        ...popupProps
    } = props

    const noTimeDate = toDate(date, true)

    const [state, setState] = React.useState(() =>
        getInitialState(date, defaultDate)
    )

    React.useEffect(() => {
        if (state.date?.valueOf() != date?.valueOf()) {
            setState(getInitialState(date, defaultDate))
        }
    }, [date, state.date])

    const range = useRangeFunctions(date, rangeClosingDate)

    const { rows, year, month } = state
    const day = isDate(date) ? date.getDate() : 1

    const isCurrent = (d: Date) => (noTimeDate && (noTimeDate.valueOf() == d.valueOf()))

    const setToday = () => {
        const n = new Date(Date.now())
        handleDateChange(
            new Date(n.getFullYear(), n.getMonth(), n.getDate())
        )
    }

    const clear = () => handleDateChange(null)

    const selectYear = (year: number) => {
        setState(s => ({ ...s, ...getStateAtYearMonth(year, month) }))
    }

    const selectMonth = (month: number) => {

        let m = month - 1
        let y = year

        if (m === -1) {
            m = 11
            y--
        }
        else if (m === 12) {
            m = 0
            y++
        }

        let d = new Date(y, m, day)

        if (d.getMonth() > m) {
            d = new Date(y, m + 1, 1).addDays(-1)
        }

        month = d.getMonth() + 1

        setState(s => ({ ...s, ...getStateAtYearMonth(d.getFullYear(), month) }))

    }

    const selectDate = (date: Date) => {
        state.date = date
        handleDateChange(date, "click")
    }

    function handleDateChange(date: Date, changeType?: DateChangeType) {
        if (setDate(date, changeType)) popupProps.close()
    }

    return (
        <div className="omnia-date-picker-popup form material-form">
            <table role="grid" tabIndex={0}>
                <thead>
                    <tr className="selector">
                        <th colSpan={7}>
                            <div>
                                <MonthSelector month={month} selectMonth={selectMonth} />
                                <div className="year">
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={year}
                                        onChange={(e) =>
                                            selectYear(
                                                Number(e.target.value) ||
                                                year
                                            )
                                        }
                                    ></input>
                                </div>
                            </div>
                        </th>
                    </tr>
                    <tr className="weekdays">
                        {weekdayNames.map((n) => (
                            <th key={n}>{n}</th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {rows.map((r, index) => (
                        <tr key={index} role="row" tabIndex={index + 1}>
                            {r.map((d, index) => (
                                <td
                                    key={d.date.valueOf()}
                                    tabIndex={index}
                                    className={classes({
                                        current: isCurrent(d.date),
                                        dateRange: range.isInDateRange(d.date),
                                        leftDateRangeEnd: range.isLeftDateRangeEnd(d.date),
                                        rightDateRangeEnd: range.isRightDateRangeEnd(d.date),
                                        disabled: d.disabled,
                                    })}
                                >
                                    <button
                                        type="button"
                                        onClick={() => selectDate(d.date)}
                                        className="btn btn-link"
                                        tabIndex={0}
                                    >
                                        {d.text}
                                    </button>
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>

                <tfoot>
                    <tr>
                        <th colSpan={7}>
                            <div className="l">
                                <div className="btn-group">
                                    <button
                                        type="button"
                                        className="btn btn-link"
                                        onClick={setToday}
                                    >
                                        Today
                                    </button>
                                </div>
                                <div className="btn-group">
                                    <button
                                        type="button"
                                        className="btn btn-link"
                                        onClick={clear}
                                    >
                                        Clear
                                    </button>
                                </div>
                                <div className="btn-group">
                                    <button
                                        type="button"
                                        className="btn btn-link"
                                        onClick={popupProps.close}
                                    >
                                        Ok
                                    </button>
                                </div>
                            </div>
                        </th>
                    </tr>
                </tfoot>
            </table>
        </div>
    )
}

function getInitialState(date?: Date, defaultDate?: Date) {

    defaultDate = defaultDate || new Date(Date.now())

    const d = date || defaultDate

    const month = d.getMonth() + 1
    const year = d.getFullYear()

    const rows = getCalendarRows(year, month)

    return {
        date,
        month,
        year,
        rows
    }

}

function getStateAtYearMonth(year: number, month: number) {
    return {
        year,
        month,
        rows: getCalendarRows(year, month)
    }
}

function getCalendarRows(year: number, month: number) {

    month -= 1

    const month1st = new Date(year, month, 1)
    const offset = -weekdays[month1st.getDay()]

    const rows = [-1, 0, 1, 2, 3, 4, 5]
        .map(i => weekdays.map((_, index) => {
            const date = month1st.addDays(offset + i * 7 + index)

            return {
                date,
                text: date.toLocaleDateString(undefined, {
                    day: "2-digit",
                }),
                disabled: date.getMonth() != month,
            }
        }))

    return rows

}

const weekdayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

const weekdays = [6, 0, 1, 2, 3, 4, 5]
